import { useState, useEffect, useCallback, useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { PartyPopper } from 'lucide-react';

import { emailService } from '@/services';
import { triggerProcessing } from '@/services/email.service';
import type { Email } from '@/types';
import { SearchInput, FilterChipGroup } from '@/components/ui';
import {
  ReviewCard,
  type EmailStatus,
} from '@/components/domain/shared/ReviewCard';
import { DiscardEmailModal } from '@/components/domain/shared/DiscardEmailModal';
import { isReadingStatus } from '@/lib/emailReadState';
import {
  readReviewHighlight,
  REVIEW_HIGHLIGHT_MS,
} from '@/lib/reviewHighlight';
import { ReviewTabSkeleton } from '@/components/states/PageSkeletons';
import { ErrorState } from '@/components/states/ErrorState';
import { HamburgerMenuButton } from '@/components/Layout/mobile/HamburgerMenuButton';
import { useAppShell } from '@/hooks/useAppShell';
import { useEmailsChanged } from '@/hooks/useEmailsChanged';
import { useHeroScrolled } from '@/hooks/useHeroScrolled';
import { usePermissions } from '@/hooks';

import { EmailReviewModal } from './review';
import { getAttachments, getEmailLinks } from './review/_components';
import { readForwardProvenance, senderContextLabel } from '@/lib/sender';

const CARD_STATUSES: ReadonlySet<string> = new Set<EmailStatus>([
  'RECEIVED',
  'PROCESSING',
  'HELD',
  'REVIEW_REQUIRED',
  'PROCESSED',
  'ERROR',
]);

/** The feed query already drops SYSTEM / REJECTED / DUPLICATE. */
function toCardStatus(status: Email['status']): EmailStatus | undefined {
  return CARD_STATUSES.has(status) ? (status as EmailStatus) : undefined;
}

/**
 * How often the feed re-reads while a card is RECEIVED/PROCESSING and the
 * tab is visible. Extraction takes tens of seconds to a few minutes, so 15s
 * keeps the card honest without hammering the API; realtime is the fast
 * path and normally lands first.
 */
export const IN_FLIGHT_POLL_MS = 15_000;

const FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'unread', label: 'Unread' },
  { value: 'attention', label: 'Needs Attention' },
  { value: 'due_soon', label: 'Due Soon' },
] as const;

function getFilterOptions(reviewCount: number) {
  return FILTER_OPTIONS.map((opt) =>
    opt.value === 'unread' ? { ...opt, count: reviewCount } : opt,
  );
}

export function Inbox() {
  const context = useAppShell();
  const location = useLocation();
  const { householdId } = context;
  const { canReview, resolved } = usePermissions();
  const [heroRef, isScrolled] = useHeroScrolled();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [emails, setEmails] = useState<Email[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [itemCounts, setItemCounts] = useState<Map<string, number>>(new Map());
  const [summaries, setSummaries] = useState<Map<string, string>>(new Map());
  const [reviewingEmailId, setReviewingEmailId] = useState<string | null>(null);
  // Emails this user has already opened (YAR-498). Unread used to be just
  // REVIEW_REQUIRED, so every pending item stayed unread however many times
  // it was opened.
  const [readIds, setReadIds] = useState<ReadonlySet<string>>(() => new Set());
  // Try again in flight, per email: the button leaves while it runs so it
  // cannot be pressed twice (YAR-480 AC8).
  const [retryingIds, setRetryingIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  // Couldn't-read card whose discard confirmation is open (YAR-477 scope 3).
  const [discardingEmailId, setDiscardingEmailId] = useState<string | null>(
    null,
  );
  const [discardLoading, setDiscardLoading] = useState(false);

  // Opened from the email status toast (YAR-480 AC7): start at the top and
  // mark the run's cards for a moment — also when already on Review, which
  // is why this keys on the navigation, not on the path.
  const [highlightIds, setHighlightIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  useEffect(() => {
    const ids = readReviewHighlight(location.state);
    if (ids.length === 0) return;
    window.scrollTo({ top: 0 });
    setHighlightIds(new Set(ids));
    const timer = setTimeout(
      () => setHighlightIds(new Set()),
      REVIEW_HIGHLIGHT_MS,
    );
    return () => clearTimeout(timer);
  }, [location.key, location.state]);

  /**
   * Re-read the feed.
   *
   * `silent` is for refreshes the user did not ask for. It skips the loading
   * flag, because swapping the list the user is looking at for a full-page
   * skeleton every time a modal closes is worse than the staleness it fixes;
   * and it swallows failures, because a blip on a background read must not
   * throw away data already on screen in favour of an error page.
   */
  const refreshEmails = useCallback(
    async ({ silent = false }: { silent?: boolean } = {}) => {
      if (!householdId) return;
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      try {
        const data = await emailService.getEmails(householdId);
        setEmails(data);
      } catch (err) {
        if (silent) return;
        setError(
          err instanceof Error ? err : new Error('Failed to fetch emails'),
        );
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [householdId],
  );

  const fetchEmails = useCallback(() => refreshEmails(), [refreshEmails]);

  // Read state is per household and per person; loaded once per household.
  // Opening a card adds to it locally (see `openEmail`), so it never needs a
  // re-read to stay current within the session.
  useEffect(() => {
    if (!householdId) return;
    let cancelled = false;
    emailService.getReadEmailIds(householdId).then((ids) => {
      if (!cancelled) setReadIds(ids);
    });
    return () => {
      cancelled = true;
    };
  }, [householdId]);

  const openEmail = useCallback((emailId: string) => {
    setReviewingEmailId(emailId);
    // Optimistic: the dot clears as the item opens (YAR-498).
    setReadIds((prev) => {
      if (prev.has(emailId)) return prev;
      const next = new Set(prev);
      next.add(emailId);
      return next;
    });
    void emailService.markEmailRead(emailId);
  }, []);

  useEffect(() => {
    if (!householdId) {
      // Only stop showing the skeleton once resolution has FINISHED.
      // Otherwise the first paint (id still pending) renders this page's
      // empty/default state before the real data can possibly arrive.
      if (context.householdResolved) setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      if (!cancelled) await fetchEmails();
    })();
    return () => {
      cancelled = true;
    };
  }, [householdId, context.householdResolved, fetchEmails]);

  // Keep the feed live while the user is looking at it (YAR-442).
  //
  // Three refresh triggers, all silent so the list never flashes a skeleton:
  //
  // 1. Realtime: any change to an `emails` row RLS lets this user see
  //    re-runs `getEmails`. The subscription is unfiltered on purpose — the
  //    feed's `inbox_address_id IN (…)` predicate cannot be expressed as a
  //    `postgres_changes` filter, so instead of guessing at one (and
  //    silently dropping rows) the query itself stays the only filter; see
  //    `useEmailsChanged`.
  // 2. A slow poll while any card is still in flight (RECEIVED/PROCESSING).
  //    Realtime is best-effort — it gives up after a few failed
  //    resubscribes — and an in-flight card is exactly the state QA sat and
  //    watched for minutes without it ever changing. The poll stops on its
  //    own once nothing is processing.
  // 3. Re-read on return to the tab, for a run that finished while the app
  //    was backgrounded — realtime may have been paused with it.
  useEmailsChanged(() => void refreshEmails({ silent: true }), {
    enabled: !!householdId,
  });

  // HELD counts as in flight: it renders as Reading and resolves on its own.
  const hasInFlight = useMemo(
    () => emails.some((e) => isReadingStatus(e.status)),
    [emails],
  );
  useEffect(() => {
    if (!householdId || !hasInFlight) return;
    const interval = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      void refreshEmails({ silent: true });
    }, IN_FLIGHT_POLL_MS);
    return () => clearInterval(interval);
  }, [householdId, hasInFlight, refreshEmails]);

  useEffect(() => {
    if (!householdId) return;
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      void refreshEmails({ silent: true });
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [householdId, refreshEmails]);

  // Fetch review item counts and summaries after emails load
  useEffect(() => {
    if (emails.length === 0) return;
    const ids = emails.map((e) => e.id);
    emailService
      .getReviewItemCounts(ids)
      .then(setItemCounts)
      .catch(() => {});
    emailService
      .getExtractionSummaries(ids)
      .then(setSummaries)
      .catch(() => {});
  }, [emails]);

  const reviewCount = emails.filter(
    (e) => e.status === 'REVIEW_REQUIRED',
  ).length;
  // Pending AND not yet opened by this user. The tab count stays the pending
  // total (work still to do); the Unread filter counts only this.
  const isUnread = useCallback(
    (e: Email) => e.status === 'REVIEW_REQUIRED' && !readIds.has(e.id),
    [readIds],
  );
  const unreadCount = emails.filter(isUnread).length;

  const filterOptions = useMemo(
    () => getFilterOptions(unreadCount),
    [unreadCount],
  );

  const displayedEmails = useMemo(() => {
    // Exclude already-processed emails from all views
    let filtered = emails.filter((e) => e.status !== 'PROCESSED');

    // Apply filter
    if (activeFilter === 'unread') {
      filtered = filtered.filter(isUnread);
    } else if (activeFilter === 'attention') {
      filtered = filtered.filter(
        (e) => e.status === 'REVIEW_REQUIRED' || e.status === 'ERROR',
      );
    } else if (activeFilter === 'due_soon') {
      filtered = filtered.filter((e) => e.status === 'REVIEW_REQUIRED');
    }

    // Apply search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (e) =>
          (e.subject?.toLowerCase().includes(q) ?? false) ||
          e.from_email.toLowerCase().includes(q),
      );
    }

    // Most recent email first
    return filtered.sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
  }, [emails, activeFilter, searchQuery, isUnread]);

  const handleRetry = useCallback(() => {
    setError(null);
    fetchEmails();
  }, [fetchEmails]);

  const handleRetryEmail = useCallback(
    async (emailId: string) => {
      if (!householdId) return;
      setRetryingIds((prev) => new Set(prev).add(emailId));
      try {
        // Atomic and manager-gated on the server (retry_email RPC); a
        // Co-admin's retry used to half-write and leave the card in ERROR.
        await emailService.retryEmail(emailId, householdId);
        triggerProcessing().catch(() => {});
      } catch {
        // Swallowed on purpose — a failed retry leaves the email exactly
        // as it was (the RPC is one transaction), and Try again comes back
        // for another go.
        //
        // The refetch below runs on both paths: a successful retry flips
        // the card to Reading right away (and arms the in-flight poll
        // above), a failed one re-reads the true server state instead of
        // leaving whatever the card happened to be rendering. Realtime
        // will also fire for the same row, which is harmless — both reads
        // are silent and idempotent.
      } finally {
        await refreshEmails({ silent: true });
        setRetryingIds((prev) => {
          const next = new Set(prev);
          next.delete(emailId);
          return next;
        });
      }
    },
    [refreshEmails, householdId],
  );

  const handleConfirmDiscard = useCallback(async () => {
    if (!discardingEmailId) return;
    setDiscardLoading(true);
    try {
      // Removes it from Review only; the original stays in Gmail (YAR-140 AC6).
      await emailService.dismissEmail(discardingEmailId);
      setDiscardingEmailId(null);
    } catch {
      // Keep the confirmation open so the parent can try once more or cancel;
      // the card is unchanged either way.
    } finally {
      setDiscardLoading(false);
      void refreshEmails({ silent: true });
    }
  }, [discardingEmailId, refreshEmails]);

  // Error state — Figma: hero + search + filters + error message
  if (error && !loading) {
    return (
      <div className="flex flex-col min-h-full bg-surface">
        <div className="p-5">
          {context?.onOpenDrawer && (
            <HamburgerMenuButton
              onClick={context.onOpenDrawer}
              showBadge={reviewCount > 0}
            />
          )}
          <h1 className="text-[32px] font-bold leading-10 text-foreground mt-4">
            Review ({reviewCount})
          </h1>
          <p className="text-base font-medium text-muted-foreground mt-1">
            What Yaruta found from your approved senders
          </p>

          <div className="mt-4">
            <SearchInput
              placeholder="Search emails..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="mt-3">
            <FilterChipGroup
              options={filterOptions}
              value={activeFilter}
              onChange={setActiveFilter}
            />
          </div>
        </div>
        <ErrorState
          message="Unable to load emails."
          onRetry={handleRetry}
        />
      </div>
    );
  }

  // Defense-in-depth: nav already hides the Review tab for view-only
  // members, but a VIEWER (or EDITOR — Review is admin-only) could
  // still hit /review directly. Bounce them to the Today feed once role
  // is resolved.
  if (resolved && !canReview) {
    return (
      <Navigate
        to="/today"
        replace
      />
    );
  }

  return (
    <div className="flex flex-col min-h-full bg-surface">
      {/* Fixed compressed header — appears on scroll */}
      <div
        className={[
          'fixed top-0 left-0 right-0 z-[100] bg-background transition-all duration-200',
          isScrolled
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 -translate-y-full pointer-events-none',
        ].join(' ')}
      >
        <div className="flex items-center justify-center px-5 py-2">
          <div className="flex-1 min-w-0">
            {context?.onOpenDrawer && (
              <HamburgerMenuButton
                onClick={context.onOpenDrawer}
                showBadge={reviewCount > 0}
                variant="compact"
              />
            )}
          </div>
          <span className="text-xl font-bold leading-6 text-foreground text-center whitespace-nowrap">
            Review ({reviewCount})
          </span>
          <div className="flex-1 min-w-0" />
        </div>
        <div className="px-5 pb-3 flex flex-col gap-3">
          <SearchInput
            placeholder="Search emails..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <FilterChipGroup
            options={filterOptions}
            value={activeFilter}
            onChange={setActiveFilter}
          />
        </div>
        <div className="h-px bg-border" />
      </div>

      {/* Scrollable content */}
      <div className="p-5 flex flex-col gap-5 pb-24">
        {/* Hero section */}
        <div ref={heroRef}>
          {context?.onOpenDrawer && (
            <div className="mb-4">
              <HamburgerMenuButton
                onClick={context.onOpenDrawer}
                showBadge={reviewCount > 0}
              />
            </div>
          )}

          <h1 className="text-[32px] font-bold leading-10 text-foreground">
            Review ({reviewCount})
          </h1>
          <p className="text-base font-medium text-muted-foreground mt-1">
            What Yaruta found from your approved senders
          </p>
        </div>

        {/* Search */}
        <SearchInput
          placeholder="Search emails..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        {/* Filter chips */}
        <FilterChipGroup
          options={filterOptions}
          value={activeFilter}
          onChange={setActiveFilter}
        />

        {/* Loading state */}
        {loading && <ReviewTabSkeleton />}

        {/* Empty state */}
        {!loading && !error && displayedEmails.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20">
            <PartyPopper
              size={32}
              className="text-muted-foreground mb-3"
            />
            <p className="text-xl font-medium text-muted-foreground">
              All caught up!
            </p>
          </div>
        )}

        {/* Email list */}
        {!loading && !error && displayedEmails.length > 0 && (
          <div className="flex flex-col gap-3">
            {displayedEmails.map((email) => (
              <ReviewCard
                key={email.id}
                subject={email.subject || '(no subject)'}
                receivedAt={email.created_at}
                summary={summaries.get(email.id)}
                sender={email.from_email}
                {...readForwardProvenance(email.preprocessing_metadata)}
                senderOrg={senderContextLabel(email)}
                provider="Gmail"
                attachmentCount={getAttachments(email).length}
                links={getEmailLinks(email)}
                extractedItemCount={itemCounts.get(email.id) ?? 0}
                isUnread={isUnread(email)}
                status={toCardStatus(email.status)}
                // Reading cards are not tappable (Figma Status=reading).
                onClick={
                  isReadingStatus(email.status)
                    ? undefined
                    : () => openEmail(email.id)
                }
                onRetry={
                  email.status === 'ERROR'
                    ? () => void handleRetryEmail(email.id)
                    : undefined
                }
                onDiscard={
                  email.status === 'ERROR'
                    ? () => setDiscardingEmailId(email.id)
                    : undefined
                }
                retrying={retryingIds.has(email.id)}
                highlighted={highlightIds.has(email.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Discard confirmation for a couldn't-read card (Figma 11002:178280) */}
      <DiscardEmailModal
        open={!!discardingEmailId}
        loading={discardLoading}
        onConfirm={() => void handleConfirmDiscard()}
        onCancel={() => {
          if (!discardLoading) setDiscardingEmailId(null);
        }}
      />

      {/* Email Review Modal */}
      <EmailReviewModal
        emailId={reviewingEmailId ?? ''}
        open={!!reviewingEmailId}
        onClose={() => {
          setReviewingEmailId(null);
          // Closing the modal is a refresh point, not just a state reset.
          // Approve and dismiss already refetched because they change data;
          // merely LOOKING at an email was assumed to change nothing. But the
          // row can change while the modal is open — an email that was still
          // processing when it was opened, and failed while the user read it,
          // is precisely QA's report: open the card, observe the failure, come
          // back, and the list still says "processing" until a manual reload.
          // The feed has no realtime subscription to move it.
          void refreshEmails({ silent: true });
        }}
        onApproved={fetchEmails}
        onDismissed={fetchEmails}
      />
    </div>
  );
}
